"use client";

import { c as _c } from "react/compiler-runtime";
import { Masonry, useInfiniteLoader } from "masonic";
import { useCallback, useMemo } from "react";
import { tv } from "tailwind-variants";
import { useDedupedItems } from "../../hooks";
import { classNames } from "../../utils/classNames";
import { FlexColumn } from "../FlexColumn";
import { ScrollingPaginator } from "../ScrollingPaginator";
import { useMasonicRerenderKey } from "./use-masonic-rerender-key";
const gridVariants = tv({
  variants: {
    variant: {
      default: "",
      gallery: ""
    },
    size: {
      sm: classNames("grid-cols-[repeat(auto-fill,minmax(115px,1fr))]", "md:grid-cols-[repeat(auto-fill,minmax(112px,1fr))]", "lg:grid-cols-[repeat(auto-fill,minmax(125px,1fr))]", "xl:grid-cols-[repeat(auto-fill,minmax(139px,1fr))]", "2xl:grid-cols-[repeat(auto-fill,minmax(148px,1fr))]"),
      md: classNames("grid-cols-[repeat(auto-fill,minmax(177px,1fr))]", "md:grid-cols-[repeat(auto-fill,minmax(172px,1fr))]", "lg:grid-cols-[repeat(auto-fill,minmax(178px,1fr))]", "xl:grid-cols-[repeat(auto-fill,minmax(186px,1fr))]", "2xl:grid-cols-[repeat(auto-fill,minmax(218px,1fr))]")
    }
  }
});
export const Grid = t0 => {
  const $ = _c(35);
  const {
    items,
    renderItem: Item,
    itemKey,
    className,
    hasNext,
    loadNext,
    isLoadingNext,
    renderItemsWrapper: Wrapper,
    context,
    size: t1,
    variant: t2,
    columnGutter,
    maxColumnWidth
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  const variant = t2 === undefined ? "default" : t2;
  const dedupedItems = useDedupedItems(items, itemKey);
  if (variant === "gallery") {
    let t3;
    if ($[0] !== Item || $[1] !== columnGutter || $[2] !== context || $[3] !== dedupedItems || $[4] !== hasNext || $[5] !== isLoadingNext || $[6] !== itemKey || $[7] !== loadNext || $[8] !== maxColumnWidth) {
      t3 = <Gallery columnGutter={columnGutter} context={context} hasNext={hasNext} isLoadingNext={isLoadingNext} itemKey={itemKey} items={dedupedItems} loadNext={loadNext} maxColumnWidth={maxColumnWidth} renderItem={Item} />;
      $[0] = Item;
      $[1] = columnGutter;
      $[2] = context;
      $[3] = dedupedItems;
      $[4] = hasNext;
      $[5] = isLoadingNext;
      $[6] = itemKey;
      $[7] = loadNext;
      $[8] = maxColumnWidth;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    return t3;
  }
  let t3;
  if ($[10] !== className) {
    t3 = classNames("w-full", className);
    $[10] = className;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== size) {
    t4 = classNames("grid w-full grid-flow-row-dense gap-3", gridVariants({
      size
    }));
    $[12] = size;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const t5 = columnGutter ? `${columnGutter}px` : undefined;
  const t6 = maxColumnWidth ? `repeat(auto-fill, minmax(min(100%, ${maxColumnWidth}px), 1fr))` : undefined;
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = {
      gap: t5,
      gridTemplateColumns: t6
    };
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== Item || $[18] !== Wrapper || $[19] !== context || $[20] !== dedupedItems || $[21] !== itemKey) {
    t8 = Wrapper ? <Wrapper>{dedupedItems.map((item, i) => <Item context={context} index={i} item={item} key={itemKey(item, i)} />)}</Wrapper> : dedupedItems.map((item_0, i_0) => <Item context={context} index={i_0} item={item_0} key={itemKey(item_0, i_0)} />);
    $[17] = Item;
    $[18] = Wrapper;
    $[19] = context;
    $[20] = dedupedItems;
    $[21] = itemKey;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t4 || $[24] !== t7 || $[25] !== t8) {
    t9 = <div className={t4} role="grid" style={t7}>{t8}</div>;
    $[23] = t4;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== hasNext || $[28] !== isLoadingNext || $[29] !== loadNext) {
    t10 = hasNext && loadNext && <ScrollingPaginator hasNext={hasNext} isLoadingNext={Boolean(isLoadingNext)} loadNext={loadNext} />;
    $[27] = hasNext;
    $[28] = isLoadingNext;
    $[29] = loadNext;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t10 || $[32] !== t3 || $[33] !== t9) {
    t11 = <FlexColumn className={t3}>{t9}{t10}</FlexColumn>;
    $[31] = t10;
    $[32] = t3;
    $[33] = t9;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  return t11;
};
const SKELETON_OBJECT = {
  IS_SKELETON: true
};
const Gallery = ({
  items,
  renderItem: Item,
  itemKey,
  className,
  context,
  hasNext,
  loadNext,
  isLoadingNext,
  columnGutter = 12,
  maxColumnWidth = 250
}) => {
  const maybeLoadMore = useInfiniteLoader(() => hasNext && !isLoadingNext && loadNext?.());
  const rerenderKey = useMasonicRerenderKey(items);

  // Masonry doesn't support undefined items well so use a placeholder token
  const itemsWithSkeletons = useMemo(() => items.map(item => {
    if (item === undefined) {
      return SKELETON_OBJECT;
    }
    return item;
  }), [items]);
  const masonryItemKey = useCallback((item_0, index) => {
    if (item_0 === SKELETON_OBJECT || item_0 === undefined) {
      return index;
    }
    return itemKey(item_0, index) ?? index;
  }, [itemKey]);
  const RenderItem = useCallback(({
    index: index_0,
    data
  }) => {
    return <Item context={context} index={index_0} item={data === SKELETON_OBJECT ? undefined : data} />;
  }, [context]);
  return <Masonry className={className} columnGutter={columnGutter} columnWidth={maxColumnWidth} itemKey={masonryItemKey} items={itemsWithSkeletons} key={rerenderKey} onRender={maybeLoadMore} render={RenderItem} />;
};